import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { ActionButtons, DeleteConfirmationModal } from "@components";

import { PostingRecordPostingForm } from "./PostingRecordPostingForm";

import { PostingRecordPostingType } from "@type/HRM/postings";
import {
  POSTING_RECORDS,
  postingRecordPostingValidation,
} from "@validations/HRM/employee/postingsValidation";
import {
  convertDocumentData,
  revertDocumentData,
} from "@utils/documentDataConvert";

interface PostingRecordPostingPropsType {
  postingRecordPostingInfo: PostingRecordPostingType;
  changePostingRecordPostingsInfo: (data: PostingRecordPostingType) => void;
  removePostingRecordPostingInfo: (postingRecordPostingID: number) => void;
  getPostingRequestOngoing: boolean;
  updatePostingRecordPostingRequestOngoing: boolean;
  deletePostingRecordPostingRequestOngoing: boolean;
  grades: any;
  isProfileDisabled: boolean;
}

export const PostingRecordPosting = ({
  postingRecordPostingInfo,
  changePostingRecordPostingsInfo,
  removePostingRecordPostingInfo,
  getPostingRequestOngoing,
  updatePostingRecordPostingRequestOngoing,
  deletePostingRecordPostingRequestOngoing,
  grades,
  isProfileDisabled,
}: PostingRecordPostingPropsType) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  const closeConfirmationModal = () => setIsConfirmationModalOpen(false);

  const deleteAction = () => {
    closeConfirmationModal();
    removePostingRecordPostingInfo(postingRecordPostingInfo.id);
  };

  const methods = useForm<PostingRecordPostingType>({
    resolver: yupResolver(postingRecordPostingValidation),
    values: revertDocumentData(
      postingRecordPostingInfo,
    ) as PostingRecordPostingType,
  });

  const { handleSubmit, setValue } = methods;
  const onSubmit: SubmitHandler<PostingRecordPostingType> = (
    data: PostingRecordPostingType,
  ) => {
    changePostingRecordPostingsInfo(convertDocumentData(data));
  };

  let isSaving =
    getPostingRequestOngoing || updatePostingRecordPostingRequestOngoing;
  let isDeleting =
    getPostingRequestOngoing || deletePostingRecordPostingRequestOngoing;

  useEffect(() => {
    if (grades && postingRecordPostingInfo) {
      const selectedEmployee = grades.find(
        (item: any) =>
          item.salaryScaleFrom === postingRecordPostingInfo.payScaleMin,
      );

      setValue(POSTING_RECORDS.GRADE, selectedEmployee?.id as number);
    }
  }, [grades, postingRecordPostingInfo, setValue]);

  return (
    <>
      {isConfirmationModalOpen && (
        <DeleteConfirmationModal
          isModalOpen={isConfirmationModalOpen}
          action={deleteAction}
          onClose={closeConfirmationModal}
        />
      )}
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          <PostingRecordPostingForm
            grades={grades}
            isProfileDisabled={isProfileDisabled}
          />
          {!isProfileDisabled && (
            <ActionButtons
              isSaving={isSaving}
              isDeleting={isDeleting}
              deleteAction={() => setIsConfirmationModalOpen(true)}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};
